import { SiteLayout } from "@/components/SiteLayout";
import {
  getPortfolioSectionsAsync,
  parsePortfolioUrl,
  type PortfolioSection,
} from "@/config/portfolioConfig";
import { useImageProtection } from "@/hooks/useImageProtection";
import { useSEO } from "@/hooks/useSEO";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  getCloudinaryUrl,
  isCloudinaryImage,
  resolveLogoUrl,
  resolvePortfolioImageUrl,
} from "@/lib/cloudinaryUtils";
import {
  applyInstagramBrowserCSS,
  getInstagramOptimizations,
  isMobileDevice,
} from "@/lib/instagramBrowserDetection";
import { cn } from "@/lib/utils";
import { trackImageOpen, trackImageClose } from "@/lib/imageViewTracking";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function portfolioStylesCss(isDark: boolean): string {
  const itemBg = isDark ? "#171717" : "#f5f5f5";
  const beforeBg = isDark
    ? "rgba(255, 255, 255, 0.06)"
    : "rgba(0, 0, 0, 0.05)";
  const activeShadow = isDark
    ? "0 4px 12px rgba(0, 0, 0, 0.45)"
    : "0 4px 12px rgba(0, 0, 0, 0.15)";
  const hoverShadow = isDark
    ? "0 8px 16px rgba(0, 0, 0, 0.5)"
    : "0 8px 16px rgba(0, 0, 0, 0.1)";

  return `
  .portfolio-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 8px;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 16px;
  }
  
  .portfolio-item {
    position: relative;
    overflow: hidden;
    background: ${itemBg};
    aspect-ratio: 4 / 3;
    transition: opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    border-radius: 2px;
  }
  
  .portfolio-item::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${beforeBg};
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 1;
    pointer-events: none;
  }
  
  /* Fotos horizontales: ocupan 2 columnas, ratio 16:9 para apreciar el formato */
  .portfolio-item.portfolio-item-wide {
    aspect-ratio: 16 / 9;
  }
  
  @media (min-width: 640px) {
    .portfolio-item.portfolio-item-wide {
      grid-column: span 2;
    }
  }
  
  .portfolio-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    image-rendering: -webkit-optimize-contrast;
    transform: translateZ(0);
  }
  
  /* Mobile touch feedback */
  .portfolio-item:active {
    opacity: 0.85;
    transform: scale(0.98);
    box-shadow: ${activeShadow};
  }
  
  .portfolio-item:active::before {
    opacity: 1;
  }
  
  @media (min-width: 640px) {
    .portfolio-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      padding: 0 24px;
    }
  }
  
  @media (min-width: 1024px) {
    .portfolio-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      padding: 0 40px;
    }
    
    .portfolio-item.portfolio-item-wide {
      grid-column: span 2;
    }
  }
  
  @media (min-width: 1280px) {
    .portfolio-grid {
      gap: 20px;
    }
  }
  
  /* Desktop hover effect */
  @media (hover: hover) and (pointer: fine) {
    .portfolio-item:hover {
      transform: translateY(-2px);
      box-shadow: ${hoverShadow};
    }
    
    .portfolio-item:hover img {
      transform: translateZ(0) scale(1.05);
    }
  }
  
  /* Safari/iOS: viewport height con fallbacks */
  .portfolio-lightbox-viewport {
    height: 100vh;
    min-height: -webkit-fill-available;
  }
  @supports (height: 100dvh) {
    .portfolio-lightbox-viewport {
      height: 100dvh;
    }
  }
  .portfolio-lightbox-viewport .portfolio-touch-area {
    height: 100vh;
    min-height: -webkit-fill-available;
  }
  @supports (height: 100dvh) {
    .portfolio-lightbox-viewport .portfolio-touch-area {
      height: 100dvh;
    }
  }
  
  /* Lightbox navigation arrows - Enhanced for mobile */
  .portfolio-nav-button {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  
  .portfolio-nav-button:active {
    transform: scale(0.92) translateY(-50%);
  }
  
  @media (hover: hover) and (pointer: fine) {
    .portfolio-nav-button:hover {
      background: rgba(255, 255, 255, 0.35) !important;
      transform: scale(1.08) translateY(-50%);
    }
  }
  
  /* Close button enhanced */
  .portfolio-close-button {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  
  .portfolio-close-button:active {
    transform: scale(0.9);
  }
  
  @media (hover: hover) and (pointer: fine) {
    .portfolio-close-button:hover {
      background: rgba(255, 255, 255, 0.3) !important;
      transform: scale(1.1);
    }
  }
  
  /* Lightbox fade in/out */
  .portfolio-lightbox-viewport {
    animation: portfolioLightboxFadeIn 0.25s ease-out;
  }
  
  @keyframes portfolioLightboxFadeIn {
    from {
      opacity: 0;
      background-color: rgba(0, 0, 0, 0);
    }
    to {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.98);
    }
  }
`;
}

const Portfolio = () => {
  const { t } = useTranslation();
  useSEO("portfolio");
  useImageProtection();
  const { isBaseTemplateBlack: isDarkShell } = useTemplateSelection();
  const portfolioCss = useMemo(
    () => portfolioStylesCss(isDarkShell),
    [isDarkShell],
  );
  const [sections, setSections] = useState<PortfolioSection[]>([]);
  /** false = desactivado desde /admin/portfolio (no mostrar imágenes en público) */
  const [portfolioPublicEnabled, setPortfolioPublicEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [imageLoading, setImageLoading] = useState(true);
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const touchContainerRef = useRef<HTMLDivElement>(null);
  const instagramOpts = getInstagramOptimizations();

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-portfolio-theme", "");
    style.textContent = portfolioCss;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) document.head.removeChild(style);
    };
  }, [portfolioCss]);

  // Instagram/Safari mobile: aplicar estilos de optimización
  useEffect(() => {
    if (getInstagramOptimizations().isInstagramBrowser) {
      applyInstagramBrowserCSS();
    }
  }, []);

  // Load sections from DB or env vars (forceRefresh so admin saves appear immediately)
  useEffect(() => {
    const loadSections = async () => {
      try {
        const { sections: loadedSections, portfolioPublicEnabled: pub } =
          await getPortfolioSectionsAsync(true);
        setSections(loadedSections);
        setPortfolioPublicEnabled(pub);
      } catch (error) {
        console.error("Error loading portfolio sections:", error);
        setSections([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadSections();
  }, []);

  // Safari/iOS: touch events con passive: false para que preventDefault funcione
  const handlersRef = useRef<{
    navigatePrev: () => void;
    navigateNext: () => void;
    closeLightbox: () => void;
    instagramOpts: ReturnType<typeof getInstagramOptimizations>;
  } | null>(null);

  const selectedSectionData =
    selectedSection !== null ? sections[selectedSection] : null;
  const currentEntries = selectedSectionData
    ? selectedSectionData.urls
        .map((raw) => parsePortfolioUrl(raw.trim()))
        .filter(
          (e) =>
            e.url &&
            isCloudinaryImage(e.url) &&
            !failedUrls.has(e.url),
        )
    : [];
  const currentUrls = currentEntries.map((e) => e.url);
  const currentIndex = Math.min(
    selectedIndex,
    Math.max(0, currentUrls.length - 1),
  );
  const currentUrl = currentUrls[currentIndex] ?? null;

  const navigatePrev = useCallback(() => {
    if (selectedSection === null || currentUrls.length === 0) return;
    setImageLoading(true);
    setSelectedIndex((i) => (i > 0 ? i - 1 : currentUrls.length - 1));
  }, [selectedSection, currentUrls.length]);

  const navigateNext = useCallback(() => {
    if (selectedSection === null || currentUrls.length === 0) return;
    setImageLoading(true);
    setSelectedIndex((i) => (i < currentUrls.length - 1 ? i + 1 : 0));
  }, [selectedSection, currentUrls.length]);

  const closeLightbox = useCallback(() => {
    setSelectedSection(null);
    setSelectedIndex(0);
    setImageLoading(true);
  }, []);

  handlersRef.current = {
    navigatePrev,
    navigateNext,
    closeLightbox,
    instagramOpts,
  };

  // Per-photo dwell + one-session-summary Slack tracking (shared across templates).
  useEffect(() => {
    if (selectedSection === null || !currentUrl) {
      trackImageClose("close");
      return;
    }
    trackImageOpen(
      {
        publicId: currentUrl,
        title: selectedSectionData?.title,
        photoId: `${selectedSection}:${currentIndex}`,
      },
      { templateName: "Portfolio" },
    );
  }, [selectedSection, currentUrl, currentIndex, selectedSectionData?.title]);

  // Solo imágenes Cloudinary (con watermark). public_id corto → anteponer carpeta de sección.
  const resolveUrl = useCallback(
    (u: string, sectionTitle?: string) =>
      resolvePortfolioImageUrl(u, sectionTitle) ?? resolveLogoUrl(u) ?? u,
    []
  );

  const getOptimizedSrc = useCallback(
    (url: string, sectionTitle?: string, width?: number) => {
      if (!isCloudinaryImage(url)) return url;
      return getCloudinaryUrl(resolveUrl(url, sectionTitle), {
        width,
        watermark: true,
      });
    },
    [resolveUrl]
  );

  // Block body scroll when lightbox open (igual que Mi Galería en BaseTemplate)
  useEffect(() => {
    if (selectedSection !== null) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;

      if (instagramOpts.isInstagramBrowser || isMobileDevice()) {
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.height = "100%";
      }
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalOverflow;
        if (instagramOpts.isInstagramBrowser || isMobileDevice()) {
          document.body.style.position = originalPosition;
          document.body.style.width = originalWidth;
          document.body.style.height = originalHeight;
        }
      };
    }
  }, [selectedSection, instagramOpts.isInstagramBrowser]);

  // Keyboard navigation
  useEffect(() => {
    if (selectedSection === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeLightbox();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigatePrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigateNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedSection, navigatePrev, navigateNext, closeLightbox]);

  // Preload adjacent images for better UX
  useEffect(() => {
    if (selectedSection === null || !selectedSectionData) return;
    
    const preloadImage = (url: string) => {
      const img = new Image();
      const optimizedSrc = selectedSectionData
        ? getOptimizedSrc(url, selectedSectionData.title, 1400)
        : url;
      img.src = optimizedSrc;
    };

    // Preload next and previous images
    const nextIndex = (currentIndex + 1) % currentUrls.length;
    const prevIndex = (currentIndex - 1 + currentUrls.length) % currentUrls.length;
    
    if (currentUrls[nextIndex]) preloadImage(currentUrls[nextIndex]);
    if (currentUrls[prevIndex]) preloadImage(currentUrls[prevIndex]);
  }, [selectedSection, currentIndex, currentUrls, selectedSectionData, getOptimizedSrc]);

  // Safari/iOS: addEventListener con passive: false para que preventDefault funcione en touchmove
  useEffect(() => {
    if (selectedSection === null) return;
    const el = touchContainerRef.current;
    if (!el || !handlersRef.current) return;

    const handleTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) {
        swipeStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (swipeStartRef.current && e.touches[0]) {
        const t = e.touches[0];
        const deltaX = Math.abs(t.clientX - swipeStartRef.current.x);
        const deltaY = Math.abs(t.clientY - swipeStartRef.current.y);
        if (deltaX > 10 || deltaY > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const h = handlersRef.current;
      if (!swipeStartRef.current || !h || !e.changedTouches[0]) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - swipeStartRef.current.x;
      const dy = t.clientY - swipeStartRef.current.y;
      const dt = Date.now() - swipeStartRef.current.time;
      const vx = Math.abs(dx) / Math.max(dt, 1);
      const vy = Math.abs(dy) / Math.max(dt, 1);
      
      // Ajustado para mejor detección en mobile (Safari iOS y Android)
      const swipeThreshold = h.instagramOpts.isInstagramBrowser ? 15 : 25;
      const velocityThreshold = 0.1;
      const tapThreshold = 12;

      // Swipe vertical (arriba/abajo)
      if (
        Math.abs(dy) > Math.abs(dx) &&
        Math.abs(dy) > swipeThreshold &&
        vy > velocityThreshold
      ) {
        if (dy < 0) h.navigateNext();
        else h.navigatePrev();
      } 
      // Swipe horizontal (izquierda/derecha) - más común en galerías
      else if (
        Math.abs(dx) > Math.abs(dy) &&
        Math.abs(dx) > swipeThreshold &&
        vx > velocityThreshold
      ) {
        if (dx > 0) h.navigatePrev();
        else h.navigateNext();
      } 
      // Tap para cerrar (solo si no hubo movimiento significativo)
      else if (Math.abs(dx) < tapThreshold && Math.abs(dy) < tapThreshold && dt < 300) {
        h.closeLightbox();
      }
      swipeStartRef.current = null;
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [selectedSection]);

  const handleImageError = (url: string) => {
    setFailedUrls((prev) => new Set(prev).add(url));
  };

  if (isLoading) {
    return (
      <SiteLayout>
        <div className="flex-1 flex items-center justify-center py-20">
          <Loader2
            className={cn(
              "w-8 h-8 animate-spin",
              isDarkShell ? "text-white/50" : "text-black/50",
            )}
          />
        </div>
      </SiteLayout>
    );
  }

  if (sections.length === 0) {
    return (
      <SiteLayout>
        <div className="flex-1 flex flex-col items-center justify-center px-5 py-24 min-h-[50vh]">
          <h1
            className={cn(
              "text-2xl sm:text-3xl md:text-4xl font-display font-light tracking-tight uppercase mb-4",
              isDarkShell ? "text-white" : "text-black",
            )}
          >
            {t("portfolio.title")}
          </h1>
          <p
            className={cn(
              "font-body text-sm text-center max-w-sm mb-10 leading-relaxed",
              isDarkShell ? "text-white/60" : "text-black/60",
            )}
          >
            {!portfolioPublicEnabled
              ? t("portfolio.notVisible")
              : t("portfolio.emptyComingSoon")}
          </p>
          <a
            href="/"
            className={cn(
              "font-body text-xs uppercase tracking-[0.2em] transition-colors font-light",
              isDarkShell
                ? "text-white/60 hover:text-white"
                : "text-black/60 hover:text-black",
            )}
          >
            {t("portfolio.backHomeArrow")}
          </a>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto py-8 sm:py-12 md:py-16">
        <header className="text-center mb-10 sm:mb-14 px-4">
          <h1
            className={cn(
              "text-2xl sm:text-3xl md:text-4xl font-display font-light tracking-tight uppercase mb-3",
              isDarkShell ? "text-white" : "text-black",
            )}
          >
            {t("portfolio.title")}
          </h1>
          <div className="flex items-center justify-center gap-4">
            <span
              className={cn(
                "w-12 h-px",
                isDarkShell ? "bg-white/20" : "bg-black/20",
              )}
            />
            <p
              className={cn(
                "font-body text-xs uppercase tracking-[0.2em] font-light",
                isDarkShell ? "text-white/60" : "text-black/60",
              )}
            >
              {t("portfolio.collections")}
            </p>
            <span
              className={cn(
                "w-12 h-px",
                isDarkShell ? "bg-white/20" : "bg-black/20",
              )}
            />
          </div>
        </header>

        <div className="space-y-12 sm:space-y-16 md:space-y-20">
          {sections.map((section, sectionIdx) => {
            const entries = section.urls
              .map((raw) => parsePortfolioUrl(raw.trim()))
              .filter(
                (e) =>
                  e.url &&
                  isCloudinaryImage(e.url) &&
                  !failedUrls.has(e.url),
              );
            if (entries.length === 0) return null;

            return (
              <section
                key={section.id ?? section.title ?? sectionIdx}
                className="animate-fade-in"
              >
                <div className="text-center mb-6 sm:mb-8 px-4">
                  <h2
                    className={cn(
                      "text-xl sm:text-2xl md:text-3xl font-display font-light tracking-tight uppercase",
                      isDarkShell ? "text-white" : "text-black",
                    )}
                  >
                    {section.title}
                  </h2>
                  <div
                    className={cn(
                      "w-12 h-px mx-auto mt-3",
                      isDarkShell ? "bg-white/20" : "bg-black/20",
                    )}
                  />
                </div>

                <div className="portfolio-grid">
                  {entries.map((entry, idx) => (
                    <div
                      key={`${sectionIdx}-${entry.url}-${idx}`}
                      className={`portfolio-item ${entry.wide ? "portfolio-item-wide" : ""}`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setImageLoading(true);
                          setSelectedSection(sectionIdx);
                          setSelectedIndex(idx);
                        }}
                        aria-label={`Ver ${section.title} - ${idx + 1}`}
                        className={cn(
                          "w-full h-full focus:outline-none focus:ring-2 touch-manipulation",
                          isDarkShell
                            ? "focus:ring-white/35"
                            : "focus:ring-black/30",
                        )}
                      >
                        <img
                          src={getOptimizedSrc(entry.url, section.title)}
                          srcSet={
                            resolveUrl(entry.url, section.title).includes(
                              "res.cloudinary.com",
                            )
                              ? `${getOptimizedSrc(entry.url, section.title, 400)} 400w, ${getOptimizedSrc(entry.url, section.title, 800)} 800w, ${getOptimizedSrc(entry.url, section.title, 1200)} 1200w`
                              : undefined
                          }
                          sizes={entry.wide ? "(max-width: 639px) 100vw, 66vw" : "(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"}
                          alt={`${section.title} - ${idx + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            handleImageError(entry.url);
                            const target = e.target as HTMLImageElement;
                            if (target) target.style.display = "none";
                          }}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <div className="text-center mt-12 sm:mt-16 px-4">
          <a
            href="/"
            className={cn(
              "font-body inline-block text-xs uppercase tracking-[0.2em] transition-colors font-light",
              isDarkShell
                ? "text-white/60 hover:text-white"
                : "text-black/60 hover:text-black",
            )}
          >
            {t("portfolio.backHome")}
          </a>
        </div>
      </div>

      {selectedSection !== null && currentUrl && (
        <div
          className={`portfolio-lightbox-viewport fixed inset-0 z-50 bg-black/98 select-none ${instagramOpts.cssClasses.modal || ""} ${instagramOpts.cssClasses.buttons || ""}`}
          style={{
            touchAction: "pan-y pan-x",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          } as React.CSSProperties}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="portfolio-close-button absolute w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-white bg-white/15 rounded-full z-30 focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation shadow-lg"
            style={{
              top: "max(1rem, env(safe-area-inset-top, 0px))",
              right: "max(1rem, env(safe-area-inset-right, 0px))",
            }}
            aria-label={t("portfolio.close")}
          >
            <X className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
          </button>

          <div
            className="font-body absolute left-1/2 -translate-x-1/2 z-30 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white text-xs sm:text-sm font-light tracking-[0.15em] uppercase shadow-lg"
            style={{ top: "max(1rem, env(safe-area-inset-top, 0px))" }}
          >
            {currentIndex + 1} / {currentUrls.length}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigatePrev();
            }}
            onTouchEnd={(e) => e.stopPropagation()}
            className="portfolio-nav-button absolute left-3 sm:left-6 top-1/2 w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center text-white bg-white/25 rounded-full z-30 focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation shadow-lg"
            style={{ transform: 'translateY(-50%)' }}
            aria-label={t("portfolio.previous")}
          >
            <ChevronLeft className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.5} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigateNext();
            }}
            onTouchEnd={(e) => e.stopPropagation()}
            className="portfolio-nav-button absolute right-3 sm:right-6 top-1/2 w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center text-white bg-white/25 rounded-full z-30 focus:outline-none focus:ring-2 focus:ring-white/40 touch-manipulation shadow-lg"
            style={{ transform: 'translateY(-50%)' }}
            aria-label={t("portfolio.next")}
          >
            <ChevronRight className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.5} />
          </button>

          <div
            ref={touchContainerRef}
            className="portfolio-touch-area relative w-full h-full flex items-center justify-center overflow-hidden z-10"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <Loader2 className="w-10 h-10 animate-spin text-white/60" />
              </div>
            )}
            <div className="relative w-full h-full flex items-center justify-center px-2">
              <img
                key={currentUrl}
                src={
                  selectedSectionData
                    ? getOptimizedSrc(
                        currentUrl!,
                        selectedSectionData.title,
                        1400,
                      )
                    : currentUrl
                }
                alt={`${sections[selectedSection].title} - ${currentIndex + 1}`}
                className="max-w-full max-h-full object-contain"
                style={{
                  maxHeight: "min(85vh, 85dvh)",
                  animation: "portfolioScaleIn 0.3s ease-out",
                  opacity: imageLoading ? 0 : 1,
                  transition: 'opacity 0.2s ease-in-out'
                }}
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
              />
            </div>
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/60 pointer-events-none md:hidden px-4 py-2 bg-black/30 backdrop-blur-sm rounded-full"
              style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
            >
              <span className="font-body text-xs tracking-wide font-light text-center">
                {t("portfolio.swipeHint")}
              </span>
            </div>
          </div>

          <style>{`
            @keyframes portfolioScaleIn {
              from { 
                opacity: 0.5; 
                transform: scale(0.96) translateZ(0); 
              }
              to { 
                opacity: 1; 
                transform: scale(1) translateZ(0); 
              }
            }
            
            /* Optimize animations for mobile */
            @media (prefers-reduced-motion: reduce) {
              @keyframes portfolioScaleIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              .portfolio-item img,
              .portfolio-nav-button,
              .portfolio-close-button {
                transition: none;
              }
            }
          `}</style>
        </div>
      )}
    </SiteLayout>
  );
};

export default Portfolio;
